import React, { useEffect, useState } from "react";
import { useAuth } from '../App';
import { hasPermission } from "../utils/permissions";
import Forbidden403 from "../components/common/Forbidden403";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import { API_URL } from "../config";

export default function StatusesPage() {
  const { auth, fetchWithAuth } = useAuth();
  // Permission check: superuser or has 'statuses:list' permission
  const hasListPermission = hasPermission(auth?.user, "status.list");
  if (!hasListPermission) {
    return <Forbidden403 />;
  }
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const fetchStatuses = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_URL}/statuses/`);
      if (!res.ok) throw new Error("Failed to fetch statuses");
      const data = await res.json();
      setStatuses(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_URL}/statuses/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, description }),
      });
      if (!res.ok) throw new Error("Failed to create status");
      setName("");
      setDescription("");
      fetchStatuses();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_URL}/statuses/${id}`, {
        method: "DELETE",
      });
      if (!res.ok && res.status !== 204)
        throw new Error("Failed to delete status");
      fetchStatuses();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (status) => {
    setEditingId(status.id);
    setEditName(status.name);
    setEditDescription(status.description || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
  };

  const handleUpdate = async (id) => {
    setError("");
    try {
      const res = await fetchWithAuth(`${API_URL}/statuses/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          name: editName, 
          description: editDescription 
        }),
      });
      if (!res.ok) throw new Error("Failed to update status");
      setEditingId(null);
      fetchStatuses();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 4, maxWidth: 700, mx: "auto" }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Statuses
      </Typography>
      <Paper sx={{ p: 3, mb: 4 }}>
        <form
          onSubmit={handleCreate}
          style={{ display: "flex", gap: 16, alignItems: "center" }}
        >
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            size="small"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={creating || !name}
          >
            Create
          </Button>
        </form>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
      <Paper>
        {loading ? (
          <CircularProgress sx={{ m: 3 }} />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statuses.map((status) => (
                  <React.Fragment key={status.id}>
                    {editingId === status.id ? (
                      <TableRow>
                        <TableCell>
                          <TextField
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            size="small"
                            fullWidth
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            aria-label="save"
                            color="primary"
                            size="small"
                            onClick={() => handleUpdate(status.id)}
                            disabled={!editName}
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            aria-label="cancel"
                            size="small"
                            onClick={handleCancelEdit}
                          >
                            <CloseIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell>{status.name}</TableCell>
                        <TableCell>{status.description}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            aria-label="edit"
                            color="primary"
                            size="small"
                            onClick={() => handleEdit(status)}
                            disabled={deletingId !== null}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            color="error"
                            size="small"
                            onClick={() => handleDelete(status.id)}
                            disabled={deletingId === status.id || editingId !== null}
                          >
                            {deletingId === status.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
